import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  GoogleMap,
  LoadScriptNext,
  CircleF,
  OverlayView
} from "@react-google-maps/api";
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
  Text,
  HStack,
  IconButton,
  useToast,
  Flex,
  Checkbox,
  Select,
  Stack,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  FormControl,
  FormLabel,
  Textarea,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  CloseButton,
  useDisclosure
} from "@chakra-ui/react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaPhone,
  FaShieldAlt,
  FaLayerGroup,
  FaExclamationTriangle,
  FaFire,
  FaBolt,
  FaCarCrash,
  FaRoad,
  FaRobot,
  FaBell,
  FaMapMarkedAlt
} from "react-icons/fa";
import { MapContainer, TileLayer, Circle, Marker, Popup } from "react-leaflet";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../api";
import Hls from "hls.js";
import ImageNetworkScene from "./ImageNetworkScene";
import { useBranding } from "../context/BrandingContext";

const getContainerStyle = (height) => ({
  width: "100%",
  height: height || "70vh",
  minHeight: "320px"
});
const defaultCenter = { lat: -21.4779, lng: -46.3919 };
const defaultZoom = 12;
const MAX_IMAGE_USERS = 50;

const mapOptions = {
  disableDefaultUI: true,
  clickableIcons: false,
  gestureHandling: "greedy"
};

const buildLatLngBounds = (center, raioKm) => {
  if (!center || !raioKm) return null;
  const latDelta = raioKm / 111;
  const lngDelta = raioKm / (111 * Math.cos((center.lat * Math.PI) / 180));
  return {
    north: center.lat + latDelta,
    south: center.lat - latDelta,
    east: center.lng + lngDelta,
    west: center.lng - lngDelta
  };
};

const normalizeCity = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();

const resolveCityState = (cidade, estado) => {
  const explicitState = String(estado || "").trim().toUpperCase();
  if (explicitState) return explicitState;
  const normalizedCity = normalizeCity(cidade);
  if (normalizedCity === "caldas" || normalizedCity === "cabo verde") {
    return "MG";
  }
  return "";
};

const formatCityLabel = (cidade, estado, separator = ", ") => {
  const city = String(cidade || "").trim();
  const state = resolveCityState(cidade, estado);
  if (!city) return "";
  return state ? `${city}${separator}${state}` : city;
};

const formatCityHeading = (cidade, estado) => formatCityLabel(cidade, estado, " - ");

const matchesSelectedCity = (user, cityKey, fallbackCityKey) => {
  const userCityKey = normalizeCity(user?.cidade);
  if (userCityKey) {
    return userCityKey === cityKey;
  }
  return Boolean(fallbackCityKey) && cityKey === fallbackCityKey;
};

const isCameraActive = (camera) => {
  if (!camera) return false;
  if (camera.ativa === undefined || camera.ativa === null || camera.ativa === "") {
    return true;
  }
  const normalized = String(camera.ativa).trim().toLowerCase();
  return Number(camera.ativa) === 1 || camera.ativa === true || ["ativo", "true", "sim"].includes(normalized);
};

const buildCameraHlsUrl = (user, camera) => {
  const fallbackKey = `${user?.usuario_id || user?.id}_${camera?.id || camera?.canal || 1}`;
  const rtmp = String(camera?.rtmp_link || "").trim();
  const streamMatch = rtmp.match(/\/live\/([^/?#]+)$/i);
  const streamKey = streamMatch?.[1] || fallbackKey;
  return `https://www.estrelacaldense.com.br:8443/hls/${streamKey}.m3u8`;
};

const distanceKm = (pointA, pointB) => {
  if (!pointA || !pointB) return null;

  const lat1 = Number(pointA.lat);
  const lng1 = Number(pointA.lng);
  const lat2 = Number(pointB.lat);
  const lng2 = Number(pointB.lng);

  if (
    !Number.isFinite(lat1) ||
    !Number.isFinite(lng1) ||
    !Number.isFinite(lat2) ||
    !Number.isFinite(lng2)
  ) {
    return null;
  }

  const toRad = (value) => (value * Math.PI) / 180;
  const R = 6371;

  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};


const LeafletFallback = ({
  usuarios,
  plano,
  center,
  userCenter,
  height,
  apiBase,
  onSelectUser,
  panicEvents,
  ocorrenciaEvents,
  onSelectPanic,
  panicUserIds
}) => {
  const raioKm = plano?.visibilidade_total ? 0 : Number(plano?.raio_km || 0);
  const mapCenter = center || defaultCenter;
  const geoCenter = userCenter || mapCenter;

  const containerStyle = getContainerStyle(height);
  const buildAvatarIcon = (url) =>
    L.divIcon({
      className: "avatar-marker",
      html: `<img src="${url}" alt="avatar" style="width:48px;height:48px;border-radius:50%;border:2px solid #ffffff;box-shadow:0 2px 6px rgba(0,0,0,0.2);" />`,
      iconSize: [48, 48],
      iconAnchor: [24, 24]
    });
  const buildPanicAvatarIcon = (url) =>
    L.divIcon({
      className: "panic-pulse-marker",
      html:
        `<div class="panic-avatar-wrapper" style="width:48px;height:48px;display:flex;align-items:center;justify-content:center;overflow:visible;">` +
        `<div class="panic-avatar-ring" style="width:64px;height:64px;top:-8px;left:-8px;"></div>` +
        `<img class="panic-avatar-img" src="${url}" alt="panic" style="width:48px;height:48px;border-radius:50%;border:2px solid #ffffff;object-fit:cover;" />` +
        `</div>`,
      iconSize: [64, 64],
      iconAnchor: [32, 32]
    });
  const buildOcorrenciaRingIcon = () =>
    L.divIcon({
      className: "ocorrencia-pulse-marker",
      html:
        `<div class="ocorrencia-avatar-wrapper" style="width:48px;height:48px;display:flex;align-items:center;justify-content:center;overflow:visible;">` +
        `<div class="ocorrencia-avatar-ring" style="width:58px;height:58px;top:-5px;left:-5px;"></div>` +
        `</div>`,
      iconSize: [58, 58],
      iconAnchor: [29, 29]
    });
  const panicPositions = (panicEvents || []).filter(
    (event) => event.latitude && event.longitude
  );
  const ocorrenciaPositions = (ocorrenciaEvents || []).filter(
    (event) => event.latitude && event.longitude
  );

  return (
    <MapContainer center={[mapCenter.lat, mapCenter.lng]} zoom={defaultZoom} style={containerStyle}>
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      {raioKm > 0 && (
        <Circle
          center={[geoCenter.lat, geoCenter.lng]}
          radius={raioKm * 1000}
          pathOptions={{ color: "#2f855a", fillColor: "#68d391", fillOpacity: 0.15 }}
        />
      )}
      {usuarios
        .filter((user) => !(panicUserIds || new Set()).has(Number(user?.usuario_id || user?.id || 0)))
        .map((user) => {
        const userId = Number(user?.usuario_id || user?.id || 0);
        if (!userId) return null;
        const avatarUrl = `${apiBase}/usuarios/${userId}.png`;
        const markerLat = Number(user.markerLatitude ?? user.latitude);
        const markerLng = Number(user.markerLongitude ?? user.longitude);
        return (
          <Marker
            key={userId}
            position={[markerLat, markerLng]}
            icon={buildAvatarIcon(avatarUrl)}
            eventHandlers={{
              click: () => {
                if (onSelectUser) {
                  onSelectUser(userId);
                }
              }
            }}
          >
            <Popup>
              <strong>{user.nome}</strong>
              <br />
              {user.email}
            </Popup>
          </Marker>
        );
      })}
      {ocorrenciaPositions.map((event) => (
        <Marker
          key={`occ-${event.id}`}
          position={[Number(event.latitude), Number(event.longitude)]}
          icon={buildOcorrenciaRingIcon()}
          eventHandlers={{
            click: () => {
              if (onSelectPanic) {
                onSelectPanic(event);
              }
            }
          }}
        />
      ))}
      {panicPositions.map((event) => (
        <Marker
          key={`panic-${event.id}`}
          position={[Number(event.latitude), Number(event.longitude)]}
          icon={buildPanicAvatarIcon(`${apiBase}/usuarios/${event.usuario_id}.png`)}
          eventHandlers={{
            click: () => {
              if (onSelectPanic) {
                onSelectPanic(event);
              }
            }
          }}
        />
      ))}
    </MapContainer>
  );
};

const MapaUsuarios = ({ height }) => {
  const [usuarios, setUsuarios] = useState([]);
  const [ocorrencias, setOcorrencias] = useState([]);
  const [aiEvents, setAiEvents] = useState([]);
  const [mapaLoading, setMapaLoading] = useState(true);
  const [plano, setPlano] = useState(null);
  const [shareRules, setShareRules] = useState([]);
  const [indexAtual, setIndexAtual] = useState(0);
  const [modalAberto, setModalAberto] = useState(false);
  const [cameraIndex, setCameraIndex] = useState(0);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [modalClock, setModalClock] = useState(() => new Date());
  const [panicDialogOpen, setPanicDialogOpen] = useState(false);
  const [panicCountdown, setPanicCountdown] = useState(10);
  const [panicSending, setPanicSending] = useState(false);
  const [ocorrenciaDialogOpen, setOcorrenciaDialogOpen] = useState(false);
  const [ocorrenciaForm, setOcorrenciaForm] = useState({
    usuario_id: "",
    camera_id: "",
    tipo: "Incendio",
    descricao: ""
  });
  const [ocorrenciaSaving, setOcorrenciaSaving] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const [googleFailed, setGoogleFailed] = useState(false);
  const [filters, setFilters] = useState({
    showMarkers: true,
    showOcorrencias: true,
    showPanico: true,
    showIA: true,
    empresaId: "all",
    publico: "all"
  });
  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  const { branding, currentVisual, selectedCity, assetUrl, isImageMode } = useBranding();
  const eventsDrawer = useDisclosure();
  const eventsModal = useDisclosure();

  const toast = useToast();
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const mapRef = useRef(null);
  const panicTimerRef = useRef(null);
  const panicCancelRef = useRef(null);
  const panicNotifiedRef = useRef(new Set());
  const panicInitializedRef = useRef(false);
  const ocorrenciaNotifiedRef = useRef(new Set());
  const ocorrenciaInitializedRef = useRef(false);
  const aiNotifiedRef = useRef(new Set());
  const aiInitializedRef = useRef(false);
  const previewClienteRef = useRef(false);

  const googleMapsKey =
    process.env.REACT_APP_GOOGLE_MAPS_KEY ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    "";
  const mapProvider = (process.env.REACT_APP_MAP_PROVIDER || "").toLowerCase();
  const preferLeaflet = mapProvider === "leaflet" || mapProvider === "free";
  const preferGoogle = mapProvider === "google";
  const hasGoogleKey = Boolean(googleMapsKey);
  const shouldUseGoogle =
    (preferGoogle || !preferLeaflet) && hasGoogleKey && !googleFailed;

  const apiBase = api.defaults.baseURL?.replace(/\/api$/, "") || "";

  const loadMapa = async () => {
    setMapaLoading(true);
    try {
      const response = await api.get("/v2/mapa");
      setUsuarios(response.data.usuarios || []);
      setOcorrencias(response.data.ocorrencias || []);
      setAiEvents(response.data.ai_events || []);
      setPlano(response.data.plano || null);
      setShareRules(response.data.share_rules || []);
    } catch (err) {
      try {
        const [usuariosRes, ocorrRes] = await Promise.all([
          api.get("/cameras/agrupadas-por-usuario"),
          api.get("/ocorrencias/all")
        ]);
        setUsuarios(usuariosRes.data || []);
        setOcorrencias(ocorrRes.data || []);
        setAiEvents([]);
      } catch (fallbackErr) {
        console.error("Erro ao buscar usuarios:", fallbackErr);
      }
    } finally {
      setMapaLoading(false);
    }
  };

  useEffect(() => {
    loadMapa();
  }, []);

  useEffect(() => {
    const loadMe = async () => {
      try {
        const response = await api.get("/v2/usuarios/me");
        setCurrentUserInfo(response.data || null);
      } catch (err) {
        setCurrentUserInfo(null);
      }
    };
    loadMe();
  }, []);

  useEffect(() => {
    const baseUrl = api.defaults.baseURL || "";
    if (!baseUrl) return;
    const wsUrl = baseUrl.replace(/^http/, "ws").replace(/\/api$/, "");
    if (!wsUrl) return;

    let socket;
    try {
      socket = new WebSocket(wsUrl);
    } catch (err) {
      return undefined;
    }

    socket.onmessage = (message) => {
      try {
        const payload = JSON.parse(message.data);
        if (payload?.type === "ai_event" && payload.data) {
          setAiEvents((prev) => {
            const exists = prev.some((item) => Number(item.id) === Number(payload.data.id));
            if (exists) return prev;
            return [payload.data, ...prev].slice(0, 200);
          });
        }
        if (payload?.type === "ai_event_resolved" && payload.data?.id) {
          setAiEvents((prev) => prev.filter((item) => Number(item.id) !== Number(payload.data.id)));
        }
        if (payload?.type === "panic_event" && payload.data) {
          setOcorrencias((prev) => {
            const exists = prev.some((item) => Number(item.id) === Number(payload.data.id));
            if (exists) return prev;
            return [payload.data, ...prev].slice(0, 300);
          });
        }
        if (payload?.type === "panic_resolved" && payload.data?.id) {
          setOcorrencias((prev) =>
            prev.filter((item) => !(Number(item.id) === Number(payload.data.id) && String(item.tipo) === "Panico"))
          );
        }
        if (payload?.type === "ocorrencia_event" && payload.data) {
          setOcorrencias((prev) => {
            const exists = prev.some((item) => Number(item.id) === Number(payload.data.id));
            if (exists) return prev;
            return [payload.data, ...prev].slice(0, 300);
          });
        }
        if (payload?.type === "ocorrencia_resolved" && payload.data?.id) {
          setOcorrencias((prev) => prev.filter((item) => Number(item.id) !== Number(payload.data.id)));
        }
      } catch (err) {
        // ignore invalid message
      }
    };

    return () => {
      if (socket) {
        socket.close();
      }
    };
  }, []);

  useEffect(() => {
    if (modalAberto) {
      const timer = setTimeout(() => carregarCameraAtual(), 120);
      return () => clearTimeout(timer);
    }
    if (hlsRef.current) {
      hlsRef.current.destroy();
    }
    return undefined;
  }, [modalAberto, cameraIndex, indexAtual]);

  useEffect(() => {
    if (!modalAberto) return undefined;
    const timer = setInterval(() => setModalClock(new Date()), 1000);
    return () => clearInterval(timer);
  }, [modalAberto]);

  useEffect(() => {
    if (!panicDialogOpen) {
      if (panicTimerRef.current) {
        clearInterval(panicTimerRef.current);
        panicTimerRef.current = null;
      }
      return;
    }

    setPanicCountdown(10);
    panicTimerRef.current = setInterval(() => {
      setPanicCountdown((prev) => {
        if (prev <= 1) {
          if (panicTimerRef.current) {
            clearInterval(panicTimerRef.current);
            panicTimerRef.current = null;
          }
          confirmPanic();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (panicTimerRef.current) {
        clearInterval(panicTimerRef.current);
        panicTimerRef.current = null;
      }
    };
  }, [panicDialogOpen]);

  const coerceCoord = (value) => {
    if (value === null || value === undefined) return null;
    const normalized =
      typeof value === "string" ? value.replace(",", ".").trim() : value;
    const num = Number(normalized);
    return Number.isFinite(num) ? num : null;
  };

  const usuariosFiltrados = useMemo(() => {
    let preparados = usuarios
      .filter((user) => (filters.empresaId === "all" ? true : String(user.empresa_id) === filters.empresaId))
      .map((user) => {
        const cameras = (user.cameras || [])
          .filter((camera) => isCameraActive(camera))
          .filter((camera) => {
            if (filters.publico === "publico") return Number(camera.flag_publico) === 1;
            if (filters.publico === "privado") return Number(camera.flag_publico) === 0;
            return true;
          });
        let lat = coerceCoord(user.latitude);
        let lng = coerceCoord(user.longitude);
        if (lat === null || lng === null) {
          const camWithCoords = cameras.find(
            (camera) => coerceCoord(camera.latitude) !== null && coerceCoord(camera.longitude) !== null
          );
          if (camWithCoords) {
            lat = coerceCoord(camWithCoords.latitude);
            lng = coerceCoord(camWithCoords.longitude);
          }
        }
        return { ...user, cameras, latitude: lat, longitude: lng };
      })
      .filter((user) => (user.cameras || []).length > 0 && user.latitude !== null && user.longitude !== null);

    const cityKey = normalizeCity(selectedCity || currentVisual?.cidade || currentUserInfo?.cidade || "");
    const fallbackCityKey = normalizeCity(branding.default_city);
    if (cityKey) {
      const cityUsers = preparados.filter((user) => matchesSelectedCity(user, cityKey, fallbackCityKey));
      if (cityUsers.length) {
        preparados = cityUsers;
      }
    }

    const grupos = new Map();
    preparados.forEach((user) => {
      const latKey = Number(user.latitude).toFixed(6);
      const lngKey = Number(user.longitude).toFixed(6);
      const key = `${latKey}|${lngKey}`;
      if (!grupos.has(key)) {
        grupos.set(key, []);
      }
      grupos.get(key).push(user);
    });

    return preparados.map((user) => {
      const lat = Number(user.latitude);
      const lng = Number(user.longitude);
      const latKey = lat.toFixed(6);
      const lngKey = lng.toFixed(6);
      const key = `${latKey}|${lngKey}`;
      const grupo = grupos.get(key) || [];

      if (grupo.length <= 1) {
        return {
          ...user,
          markerLatitude: lat,
          markerLongitude: lng
        };
      }

      const userId = Number(user?.usuario_id || user?.id || 0);
      const indexById = grupo.findIndex((item) => Number(item?.usuario_id || item?.id || 0) === userId);
      const markerIndex = indexById >= 0 ? indexById : grupo.indexOf(user);
      const safeMarkerIndex = markerIndex >= 0 ? markerIndex : 0;
      const spreadMeters = Math.min(34, 10 + grupo.length * 2);
      const angle = (2 * Math.PI * safeMarkerIndex) / grupo.length;
      const latOffset = (spreadMeters / 111320) * Math.sin(angle);
      const latCos = Math.cos((lat * Math.PI) / 180);
      const lngDivisor = 111320 * (Math.abs(latCos) < 1e-6 ? 1e-6 : latCos);
      const lngOffset = (spreadMeters / lngDivisor) * Math.cos(angle);

      return {
        ...user,
        markerLatitude: lat + latOffset,
        markerLongitude: lng + lngOffset
      };
    });
  }, [usuarios, filters.empresaId, filters.publico, selectedCity, currentVisual?.cidade, currentUserInfo?.cidade, branding.default_city]);

  useEffect(() => {
    if (indexAtual >= usuariosFiltrados.length) {
      setIndexAtual(0);
      setCameraIndex(0);
    }
  }, [usuariosFiltrados, indexAtual]);

  const empresasDisponiveis = useMemo(() => {
    const ids = new Set();
    usuarios.forEach((user) => ids.add(user.empresa_id));
    return Array.from(ids).filter(Boolean).sort((a, b) => a - b);
  }, [usuarios]);

  const usuarioNomeMap = useMemo(() => {
    const map = new Map();
    usuarios.forEach((user) => {
      map.set(user.usuario_id, user.nome);
    });
    return map;
  }, [usuarios]);

  const usuarioCoordMap = useMemo(() => {
    const map = new Map();
    usuarios.forEach((user) => {
      if (user.latitude && user.longitude) {
        map.set(Number(user.usuario_id), {
          latitude: user.latitude,
          longitude: user.longitude
        });
      }
    });
    return map;
  }, [usuarios]);

  const cameraCoordMap = useMemo(() => {
    const map = new Map();
    usuarios.forEach((user) => {
      (user.cameras || []).forEach((cam) => {
        if (cam.id && cam.latitude && cam.longitude) {
          map.set(Number(cam.id), {
            latitude: cam.latitude,
            longitude: cam.longitude
          });
        }
      });
    });
    return map;
  }, [usuarios]);

  const normalizeTipo = (value) => {
    if (!value) return "";
    return value
      .toString()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase();
  };

  const isPanicTipo = (value) => normalizeTipo(value).includes("panico");
  const isEncerradoStatus = (value) => {
    const normalized = normalizeTipo(value);
    return (
      normalized === "encerrada" ||
      normalized === "encerrado" ||
      normalized === "resolvido"
    );
  };

  const panicEvents = useMemo(() => {
    return ocorrencias.filter((item) => isPanicTipo(item.tipo));
  }, [ocorrencias]);

  const ocorrenciaEventos = useMemo(() => {
    return ocorrencias.filter((item) => !isPanicTipo(item.tipo) && !isEncerradoStatus(item.status));
  }, [ocorrencias]);

  const aiEventos = useMemo(() => {
    return aiEvents.filter((item) => !isEncerradoStatus(item.status));
  }, [aiEvents]);

  const panicUserIds = useMemo(() => {
    const ids = new Set();
    panicEvents.forEach((event) => {
      if (event.usuario_id) {
        ids.add(Number(event.usuario_id));
      }
    });
    return ids;
  }, [panicEvents]);

  const ocorrenciaUserIds = useMemo(() => {
    const ids = new Set();
    ocorrenciaEventos.forEach((event) => {
      if (event.usuario_id) {
        ids.add(Number(event.usuario_id));
      }
    });
    return ids;
  }, [ocorrenciaEventos]);

  const panicEventsWithCoords = useMemo(() => {
    if (!filters.showPanico) return [];
    return panicEvents
      .map((item) => {
        if (item.usuario_id && usuarioCoordMap.get(Number(item.usuario_id))) {
          return { ...item, ...usuarioCoordMap.get(Number(item.usuario_id)) };
        }
        if (item.latitude && item.longitude) return item;
        const cameraCoords = item.camera_id ? cameraCoordMap.get(Number(item.camera_id)) : null;
        if (cameraCoords) {
          return { ...item, ...cameraCoords };
        }
        return item;
      })
      .filter((item) => item.latitude && item.longitude);
  }, [panicEvents, filters.showPanico, cameraCoordMap, usuarioCoordMap]);

  const panicUserPoints = useMemo(() => {
    const points = [];
    const seen = new Set();
    panicEventsWithCoords.forEach((event) => {
      const userId = event.usuario_id ? Number(event.usuario_id) : null;
      if (!userId || seen.has(userId)) return;
      points.push(event);
      seen.add(userId);
    });
    return points;
  }, [panicEventsWithCoords]);

  const ocorrenciaEventsWithCoords = useMemo(() => {
    if (!filters.showOcorrencias) return [];
    return ocorrenciaEventos
      .map((item) => {
        if (item.usuario_id && usuarioCoordMap.get(Number(item.usuario_id))) {
          return { ...item, ...usuarioCoordMap.get(Number(item.usuario_id)) };
        }
        if (item.latitude && item.longitude) return item;
        const cameraCoords = item.camera_id ? cameraCoordMap.get(Number(item.camera_id)) : null;
        if (cameraCoords) {
          return { ...item, ...cameraCoords };
        }
        return item;
      })
      .filter((item) => item.latitude && item.longitude);
  }, [ocorrenciaEventos, filters.showOcorrencias, cameraCoordMap, usuarioCoordMap]);

  const ocorrenciaPoints = useMemo(() => {
    const points = [];
    const seen = new Set();
    ocorrenciaEventsWithCoords.forEach((event) => {
      const key = event.camera_id ? `c-${event.camera_id}` : `u-${event.usuario_id}`;
      if (seen.has(key)) return;
      points.push(event);
      seen.add(key);
    });
    return points;
  }, [ocorrenciaEventsWithCoords]);

  const ocorrenciaPointsFiltradas = useMemo(() => {
    if (!filters.showOcorrencias) return [];
    return ocorrenciaPoints.filter((event) => {
      if (!event) return false;
      const userId = event.usuario_id ? Number(event.usuario_id) : null;
      if (userId && panicUserIds.has(userId)) return false;
      return true;
    });
  }, [ocorrenciaPoints, filters.showOcorrencias, panicUserIds]);

  const aiEventsWithCoords = useMemo(() => {
    if (!filters.showIA) return [];
    return aiEventos
      .map((item) => {
        if (item.usuario_id && usuarioCoordMap.get(Number(item.usuario_id))) {
          return { ...item, ...usuarioCoordMap.get(Number(item.usuario_id)) };
        }
        if (item.latitude && item.longitude) return item;
        const cameraCoords = item.camera_id ? cameraCoordMap.get(Number(item.camera_id)) : null;
        if (cameraCoords) {
          return { ...item, ...cameraCoords };
        }
        return item;
      })
      .filter((item) => item.latitude && item.longitude);
  }, [aiEventos, filters.showIA, cameraCoordMap, usuarioCoordMap]);

  const aiPoints = useMemo(() => {
    const points = [];
    const seen = new Set();
    aiEventsWithCoords.forEach((event) => {
      const key = event.camera_id ? `ai-${event.camera_id}` : `ai-u-${event.usuario_id}`;
      if (seen.has(key)) return;
      points.push(event);
      seen.add(key);
    });
    return points;
  }, [aiEventsWithCoords]);

  const buildEventKey = (prefix, event) => {
    if (!event) return `${prefix}-unknown`;
    if (event.id) return `${prefix}-${event.id}`;
    return `${prefix}-${event.usuario_id}-${event.camera_id}-${event.tipo || "evento"}`;
  };

  const buildImageUrl = (event) => {
    const url = event?.image_url;
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${apiBase}${url}`;
  };

  const formatEventTime = (event) => {
    if (!event) return "-";
    const raw = event.detected_at || event.created_at || event.data || event.data_hora;
    if (!raw) return "-";
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return String(raw);
    return date.toLocaleString("pt-BR");
  };

  const playAlertSound = (frequency = 880, duration = 0.6) => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const ctx = new AudioContext();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = frequency;
      gain.gain.value = 0.001;
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start();
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      oscillator.stop(ctx.currentTime + duration + 0.05);
      oscillator.onended = () => {
        ctx.close();
      };
    } catch (err) {
      // ignore
    }
  };

  const showEventToast = (event, options) => {
    if (!event) return;
    const imageUrl = buildImageUrl(event);
    const userLabel =
      usuarioNomeMap.get(event.usuario_id) || `Usuario ${event.usuario_id || "-"}`;
    const cameraLabel = event.camera_id ? `Camera ${event.camera_id}` : "Camera -";
    const eventTime = formatEventTime(event);
    const toastId = `event-${buildEventKey(options.title || "evento", event)}-${Date.now()}`;

    toast({
      id: toastId,
      position: "top-right",
      duration: 6000,
      isClosable: true,
      render: () => (
        <Box
          bg="white"
          borderRadius="lg"
          boxShadow="lg"
          p={3}
          borderLeft={`4px solid ${options.color}`}
          maxW="320px"
        >
          <HStack align="start" spacing={3}>
            <Box color={options.color} fontSize="20px" mt="2px">
              <options.icon />
            </Box>
            <Box flex="1">
              <Text fontWeight="bold" fontSize="sm">
                {options.title}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {userLabel} - {cameraLabel}
              </Text>
              {event.descricao && (
                <Text fontSize="xs" color="gray.600" mt={1}>
                  {event.descricao}
                </Text>
              )}
              {eventTime !== "-" && (
                <Text fontSize="xs" color="gray.400" mt={1}>
                  {eventTime}
                </Text>
              )}
              {imageUrl && (
                <Image
                  src={imageUrl}
                  alt={options.title}
                  mt={2}
                  borderRadius="md"
                  maxH="120px"
                  objectFit="cover"
                  width="100%"
                />
              )}
            </Box>
            <CloseButton size="sm" onClick={() => toast.close(toastId)} />
          </HStack>
        </Box>
      )
    });
  };

  const playPanicSound = () => {
    playAlertSound(880, 0.65);
  };

  useEffect(() => {
    if (!panicInitializedRef.current) {
      panicEvents.forEach((event) => {
        if (!canSeeEvent(event)) return;
        const key = buildEventKey("panic", event);
        panicNotifiedRef.current.add(key);
      });
      panicInitializedRef.current = true;
      return;
    }

    let hasNew = false;
    panicEvents.forEach((event) => {
      if (!canSeeEvent(event)) return;
      const key = buildEventKey("panic", event);
      if (!panicNotifiedRef.current.has(key)) {
        panicNotifiedRef.current.add(key);
        showEventToast(event, {
          title: "Panico",
          color: "#C53030",
          icon: FaShieldAlt
        });
        hasNew = true;
      }
    });

    if (hasNew) {
      playPanicSound();
    }
  }, [panicEvents]);

  useEffect(() => {
    if (!ocorrenciaInitializedRef.current) {
      ocorrenciaEventos.forEach((event) => {
        if (!canSeeEvent(event)) return;
        const key = buildEventKey("ocorrencia", event);
        ocorrenciaNotifiedRef.current.add(key);
      });
      ocorrenciaInitializedRef.current = true;
      return;
    }

    let hasNew = false;
    ocorrenciaEventos.forEach((event) => {
      if (!canSeeEvent(event)) return;
      const key = buildEventKey("ocorrencia", event);
      if (!ocorrenciaNotifiedRef.current.has(key)) {
        ocorrenciaNotifiedRef.current.add(key);
        showEventToast(event, {
          title: "Ocorrencia",
          color: "#DD6B20",
          icon: FaExclamationTriangle
        });
        hasNew = true;
      }
    });

    if (hasNew) {
      playAlertSound(640, 0.5);
    }
  }, [ocorrenciaEventos]);

  useEffect(() => {
    if (!aiInitializedRef.current) {
      aiEventos.forEach((event) => {
        if (!canSeeEvent(event)) return;
        const key = buildEventKey("ai", event);
        aiNotifiedRef.current.add(key);
      });
      aiInitializedRef.current = true;
      return;
    }

    let hasNew = false;
    aiEventos.forEach((event) => {
      if (!canSeeEvent(event)) return;
      const key = buildEventKey("ai", event);
      if (!aiNotifiedRef.current.has(key)) {
        aiNotifiedRef.current.add(key);
        showEventToast(event, {
          title: "Violacao",
          color: "#6B46C1",
          icon: FaRobot
        });
        hasNew = true;
      }
    });

    if (hasNew) {
      playAlertSound(760, 0.55);
    }
  }, [aiEventos]);

  const currentUserId = useMemo(() => {
    const localUser = JSON.parse(localStorage.getItem("user") || "{}");
    return localUser?.id ? Number(localUser.id) : null;
  }, []);
  const currentUserRole = useMemo(() => {
    const localUser = JSON.parse(localStorage.getItem("user") || "{}");
    return localUser?.role || null;
  }, []);

  const canSeeEvent = (event) => {
    if (!event) return false;
    if (["owner", "admin", "superadmin"].includes(currentUserRole)) return true;
    return currentUserId !== null && Number(currentUserId) === Number(event.usuario_id);
  };

  const currentUser = useMemo(() => {
    const localUser = JSON.parse(localStorage.getItem("user") || "{}");
    const localUserId = Number(localUser?.id);
    return (
      usuariosFiltrados.find((user) => Number(user.usuario_id) === localUserId) ||
      usuariosFiltrados[0] ||
      null
    );
  }, [usuariosFiltrados]);

  const cidadeSelecionada = useMemo(
    () => selectedCity || currentVisual?.cidade || currentUserInfo?.cidade || currentUser?.cidade || "",
    [selectedCity, currentVisual?.cidade, currentUserInfo?.cidade, currentUser?.cidade]
  );

  const usuariosModoImagem = useMemo(() => {
    const cityKey = normalizeCity(cidadeSelecionada);
    const fallbackCityKey = normalizeCity(branding.default_city);
    if (!cityKey) {
      return usuariosFiltrados.slice(0, MAX_IMAGE_USERS);
    }
    const filtered = usuariosFiltrados.filter((user) => matchesSelectedCity(user, cityKey, fallbackCityKey));
    return (filtered.length ? filtered : usuariosFiltrados).slice(0, MAX_IMAGE_USERS);
  }, [usuariosFiltrados, cidadeSelecionada, branding.default_city]);

  const mapCenter = useMemo(() => {
    const userLat = coerceCoord(currentUserInfo?.latitude);
    const userLng = coerceCoord(currentUserInfo?.longitude);
    if (userLat !== null && userLng !== null) {
      return { lat: userLat, lng: userLng };
    }
    const fallbackLat = coerceCoord(currentUser?.latitude);
    const fallbackLng = coerceCoord(currentUser?.longitude);
    if (fallbackLat !== null && fallbackLng !== null) {
      return { lat: fallbackLat, lng: fallbackLng };
    }
    return defaultCenter;
  }, [currentUser, currentUserInfo]);

  const raioKm = plano?.visibilidade_total ? 0 : Number(plano?.raio_km || 0);

  const visiblePoints = useMemo(() => {
    const points = [];
    if (filters.showMarkers) {
      usuariosFiltrados.forEach((user) => {
        const lat = user.markerLatitude ?? user.latitude;
        const lng = user.markerLongitude ?? user.longitude;
        if (lat !== null && lng !== null) {
          points.push({ lat: Number(lat), lng: Number(lng) });
        }
      });
    }
    if (filters.showPanico) {
      panicUserPoints.forEach((event) => {
        if (event.latitude && event.longitude) {
          points.push({ lat: Number(event.latitude), lng: Number(event.longitude) });
        }
      });
    }
    if (filters.showOcorrencias) {
      ocorrenciaPointsFiltradas.forEach((event) => {
        if (event.latitude && event.longitude) {
          points.push({ lat: Number(event.latitude), lng: Number(event.longitude) });
        }
      });
    }
    if (filters.showIA) {
      aiPoints.forEach((event) => {
        if (event.latitude && event.longitude) {
          points.push({ lat: Number(event.latitude), lng: Number(event.longitude) });
        }
      });
    }
    return points;
  }, [
    filters.showMarkers,
    filters.showPanico,
    filters.showOcorrencias,
    filters.showIA,
    usuariosFiltrados,
    panicUserPoints,
    ocorrenciaPointsFiltradas,
    aiPoints
  ]);

  const displayCenter = useMemo(() => {
    if (!visiblePoints.length) {
      return mapCenter;
    }
    let minLat = visiblePoints[0].lat;
    let maxLat = visiblePoints[0].lat;
    let minLng = visiblePoints[0].lng;
    let maxLng = visiblePoints[0].lng;
    visiblePoints.forEach((point) => {
      minLat = Math.min(minLat, point.lat);
      maxLat = Math.max(maxLat, point.lat);
      minLng = Math.min(minLng, point.lng);
      maxLng = Math.max(maxLng, point.lng);
    });
    return {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2
    };
  }, [visiblePoints, mapCenter]);

  const camerasAtivas = (usuariosFiltrados[indexAtual]?.cameras || []).filter((camera) =>
    isCameraActive(camera)
  );
  const cameraAtual = camerasAtivas[cameraIndex];

  useEffect(() => {
    if (!usuariosFiltrados.length) return;
    if (indexAtual >= usuariosFiltrados.length) {
      setIndexAtual(0);
    }
  }, [indexAtual, usuariosFiltrados.length]);

  useEffect(() => {
    if (!camerasAtivas.length) return;
    if (cameraIndex >= camerasAtivas.length) {
      setCameraIndex(0);
    }
  }, [cameraIndex, camerasAtivas.length]);

  const abrirModal = (index) => {
    setIndexAtual(index);
    setCameraIndex(0);
    setModalAberto(true);
  };

  const abrirModalByUserId = (userId) => {
    const index = usuariosFiltrados.findIndex(
      (user) => Number(user.usuario_id) === Number(userId)
    );
    if (index === -1) return;
    abrirModal(index);
  };

  useEffect(() => {
    if (previewClienteRef.current) return;
    if (!usuariosFiltrados.length) return;
    const previewId = Number(localStorage.getItem("preview_cliente_id") || 0);
    if (!previewId) return;
    const exists = usuariosFiltrados.some(
      (user) => Number(user.usuario_id) === Number(previewId)
    );
    if (!exists) return;
    previewClienteRef.current = true;
    localStorage.removeItem("preview_cliente_id");
    abrirModalByUserId(previewId);
  }, [usuariosFiltrados]);

  const fecharModal = () => {
    setModalAberto(false);
    eventsModal.onClose();
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
  };

  const proximoUsuario = () => {
    if (!usuariosFiltrados.length) return;
    setIndexAtual((prev) => (prev + 1) % usuariosFiltrados.length);
    setCameraIndex(0);
  };

  const usuarioAnterior = () => {
    if (!usuariosFiltrados.length) return;
    setIndexAtual((prev) => (prev - 1 + usuariosFiltrados.length) % usuariosFiltrados.length);
    setCameraIndex(0);
  };

  const proximaCamera = () => {
    if (!camerasAtivas.length) return;
    setCameraIndex((prev) => (prev + 1) % camerasAtivas.length);
  };

  const cameraAnterior = () => {
    if (!camerasAtivas.length) return;
    setCameraIndex((prev) => (prev - 1 + camerasAtivas.length) % camerasAtivas.length);
  };

  const acionarPanico = async (camera_id) => {
    try {
      const response = await api.post("/panic/register", {
        camera_id,
        usuario_id: usuariosFiltrados[indexAtual].usuario_id
      });
      const evento = {
        id: response.data?.id || Date.now(),
        usuario_id: usuariosFiltrados[indexAtual].usuario_id,
        camera_id,
        tipo: "Panico",
        descricao: "Alerta de panico",
        latitude:
          cameraAtual?.latitude ||
          usuariosFiltrados[indexAtual].latitude ||
          null,
        longitude:
          cameraAtual?.longitude ||
          usuariosFiltrados[indexAtual].longitude ||
          null
      };
      setOcorrencias((prev) => [evento, ...prev]);
      toast({ title: "Panico enviado", status: "success", duration: 4000 });
    } catch (err) {
      toast({ title: "Erro ao acionar panico", status: "error" });
    }
  };

  const openOcorrenciaDialog = () => {
    setOcorrenciaForm({
      usuario_id: usuariosFiltrados[indexAtual]?.usuario_id || "",
      camera_id: cameraAtual?.id || "",
      tipo: "Incendio",
      descricao: ""
    });
    setOcorrenciaDialogOpen(true);
  };

  const closeOcorrenciaDialog = () => {
    setOcorrenciaDialogOpen(false);
  };

  const handleOcorrenciaSave = async () => {
    if (!ocorrenciaForm.usuario_id || !ocorrenciaForm.camera_id || !ocorrenciaForm.tipo) {
      toast({ title: "Preencha usuario, camera e tipo", status: "warning", duration: 3000 });
      return;
    }
    setOcorrenciaSaving(true);
    try {
      await api.post("/ocorrencias/register", {
        usuario_id: ocorrenciaForm.usuario_id,
        camera_id: ocorrenciaForm.camera_id,
        tipo: ocorrenciaForm.tipo,
        descricao: ocorrenciaForm.descricao || null
      });
      const usuarioCoords = usuarioCoordMap.get(Number(ocorrenciaForm.usuario_id));
      const cameraCoords = cameraCoordMap.get(Number(ocorrenciaForm.camera_id));
      setOcorrencias((prev) => [
        {
          id: Date.now(),
          usuario_id: ocorrenciaForm.usuario_id,
          camera_id: ocorrenciaForm.camera_id,
          tipo: ocorrenciaForm.tipo,
          descricao: ocorrenciaForm.descricao || "",
          latitude: cameraCoords?.latitude || usuarioCoords?.latitude || null,
          longitude: cameraCoords?.longitude || usuarioCoords?.longitude || null
        },
        ...prev
      ]);
      toast({ title: "Ocorrencia registrada", status: "success", duration: 3000 });
      setOcorrenciaDialogOpen(false);
    } catch (err) {
      toast({ title: "Erro ao registrar ocorrencia", status: "error", duration: 3000 });
    } finally {
      setOcorrenciaSaving(false);
    }
  };

  const openPanicDialog = () => {
    if (!cameraAtual) {
      toast({ title: "Selecione uma camera", status: "warning", duration: 3000 });
      return;
    }
    setPanicCountdown(10);
    setPanicDialogOpen(true);
  };

  const cancelPanicDialog = () => {
    setPanicDialogOpen(false);
    setPanicCountdown(10);
  };

  const confirmPanic = async () => {
    if (panicSending) return;
    if (!cameraAtual) {
      setPanicDialogOpen(false);
      return;
    }
    setPanicDialogOpen(false);
    setPanicSending(true);
    await acionarPanico(cameraAtual.id);
    setPanicSending(false);
  };

  const resolvePanic = async (event) => {
    if (!event?.id) return;
    try {
      await api.post("/panic/resolve", { id_alerta: event.id });
      setOcorrencias((prev) =>
        prev.filter((item) => !(item.tipo === "Panico" && item.id === event.id))
      );
      toast({ title: "Panico resolvido", status: "success", duration: 3000 });
    } catch (err) {
      toast({ title: "Erro ao resolver panico", status: "error", duration: 3000 });
    }
  };

  const resolveOcorrencia = async (event) => {
    if (!event?.id) return;
    try {
      await api.post("/ocorrencias/resolve", { id: event.id });
      setOcorrencias((prev) =>
        prev.filter((item) => !(item.id === event.id && !isPanicTipo(item.tipo)))
      );
      toast({ title: "Ocorrencia encerrada", status: "success", duration: 3000 });
    } catch (err) {
      toast({ title: "Erro ao encerrar ocorrencia", status: "error", duration: 3000 });
    }
  };

  const resolveAiEvent = async (event) => {
    if (!event?.id) return;
    try {
      await api.post("/v2/ai/events/resolve", { id: event.id });
      setAiEvents((prev) => prev.filter((item) => Number(item.id) !== Number(event.id)));
      toast({ title: "Violacao encerrada", status: "success", duration: 3000 });
    } catch (err) {
      toast({ title: "Erro ao encerrar violacao", status: "error", duration: 3000 });
    }
  };

  const openPanicEvent = (event) => {
    if (!event) return;
    const userIndex = usuariosFiltrados.findIndex(
      (user) => Number(user.usuario_id) === Number(event.usuario_id)
    );
    if (userIndex === -1) return;
    const cameras = usuariosFiltrados[userIndex].cameras || [];
    const cameraIdx = cameras.findIndex(
      (camera) => Number(camera.id) === Number(event.camera_id)
    );
    setIndexAtual(userIndex);
    setCameraIndex(cameraIdx >= 0 ? cameraIdx : 0);
    setModalAberto(true);
  };

  const canResolvePanic = (event) => {
    if (!event) return false;
    if (["owner", "admin", "superadmin"].includes(currentUserRole)) return true;
    return currentUserId !== null && Number(currentUserId) === Number(event.usuario_id);
  };

  const canResolveOcorrencia = (event) => {
    if (!event) return false;
    if (["owner", "admin", "superadmin"].includes(currentUserRole)) return true;
    return currentUserId !== null && Number(currentUserId) === Number(event.usuario_id);
  };

  const canResolveAi = (event) => {
    if (!event) return false;
    if (["owner", "admin", "superadmin"].includes(currentUserRole)) return true;
    return currentUserId !== null && Number(currentUserId) === Number(event.usuario_id);
  };

  const carregarCameraAtual = () => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    if (!cameraAtual) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      video.pause();
      video.removeAttribute("src");
      video.load();
      setLoadingVideo(false);
      return;
    }

    const videoSrc = buildCameraHlsUrl(usuariosFiltrados[indexAtual], cameraAtual);

    setLoadingVideo(true);

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    video.pause();
    video.removeAttribute("src");
    video.load();

    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(videoSrc);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
        setLoadingVideo(false);
      });
      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          setLoadingVideo(false);
          toast({
            title: "Stream offline",
            description: "Verifique se a camera esta conectada.",
            status: "error",
            duration: 6000,
            isClosable: true
          });
        }
      });
      hlsRef.current = hls;
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = videoSrc;
      video.addEventListener("loadedmetadata", () => {
        video.play().catch(() => {});
        setLoadingVideo(false);
      });
    }
  };

  const fitBounds = () => {
    if (!mapRef.current || !window.google) return;

    const bounds = new window.google.maps.LatLngBounds();
    if (visiblePoints.length) {
      visiblePoints.forEach((point) => {
        bounds.extend(point);
      });
    } else if (raioKm > 0 && mapCenter) {
      const box = buildLatLngBounds(mapCenter, raioKm);
      if (box) {
        bounds.extend({ lat: box.north, lng: box.east });
        bounds.extend({ lat: box.south, lng: box.west });
      }
    }

    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds);
      return;
    }
    if (displayCenter) {
      mapRef.current.setCenter(displayCenter);
      mapRef.current.setZoom(defaultZoom);
    }
  };

  useEffect(() => {
    if (googleReady) {
      fitBounds();
    }
  }, [googleReady, visiblePoints, raioKm, mapCenter, displayCenter]);

  useEffect(() => {
    if (!hasGoogleKey) return;
    const timer = setTimeout(() => {
      if (!window.google) {
        setGoogleFailed(true);
      }
    }, 4000);
    return () => clearTimeout(timer);
  }, [hasGoogleKey]);

  useEffect(() => {
    if (!googleReady) return;
    const handleResize = () => {
      if (mapRef.current && window.google) {
        window.google.maps.event.trigger(mapRef.current, "resize");
        if (displayCenter) {
          mapRef.current.setCenter(displayCenter);
        }
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [googleReady, displayCenter]);

  const handleToggle = (key) => {
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const containerStyle = getContainerStyle(height);

  const renderFilters = () => (
    <Box
      position="absolute"
      top={{ base: "260px", md: "70px" }}
      left="12px"
      bg="white"
      borderRadius="lg"
      boxShadow="md"
      p={3}
      maxW="260px"
      zIndex={2}
    >
      <HStack mb={2} spacing={2} align="center">
        <FaLayerGroup />
        <Text fontWeight="bold">Filtros</Text>
      </HStack>
      <Stack spacing={2}>
        <Checkbox isChecked={filters.showMarkers} onChange={() => handleToggle("showMarkers")}>
          Avatares
        </Checkbox>
        <Checkbox isChecked={filters.showOcorrencias} onChange={() => handleToggle("showOcorrencias")}>
          Ocorrencias
        </Checkbox>
        <Checkbox isChecked={filters.showPanico} onChange={() => handleToggle("showPanico")}>
          Panico
        </Checkbox>
        <Checkbox isChecked={filters.showIA} onChange={() => handleToggle("showIA")}>
          IA
        </Checkbox>
        <Select
          size="sm"
          value={filters.empresaId}
          onChange={(e) => setFilters((prev) => ({ ...prev, empresaId: e.target.value }))}
        >
          <option value="all">Todas empresas</option>
          {empresasDisponiveis.map((id) => (
            <option key={id} value={String(id)}>
              Empresa {id}
            </option>
          ))}
        </Select>
        <Select
          size="sm"
          value={filters.publico}
          onChange={(e) => setFilters((prev) => ({ ...prev, publico: e.target.value }))}
        >
          <option value="all">Publico + Privado</option>
          <option value="publico">Somente publico</option>
          <option value="privado">Somente privado</option>
        </Select>
      </Stack>
      {shareRules.length > 0 && (
        <Badge mt={2} colorScheme="purple">
          Compartilhado
        </Badge>
      )}
    </Box>
  );

  const renderEventsPanel = ({ includeIA = true } = {}) => (
    <Box
      className="camera-events-panel"
      bg="rgba(2, 24, 32, 0.82)"
      border="1px solid rgba(147, 229, 231, 0.28)"
      borderRadius="26px"
      p={3}
      backdropFilter="blur(12px)"
      boxShadow="0 20px 44px rgba(4, 24, 35, 0.35)"
      minH="0"
      h="100%"
    >
      <Tabs size="sm" variant="unstyled" isLazy h="100%" display="flex" flexDirection="column">
        <TabList
          display="grid"
          gridTemplateColumns={includeIA ? "repeat(3, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))"}
          gap={2}
          bg="rgba(255, 255, 255, 0.08)"
          p={1}
          borderRadius="full"
        >
          <Tab
            aria-label="Panico"
            color="whiteAlpha.900"
            borderRadius="full"
            _selected={{ bg: "rgba(255, 255, 255, 0.93)", color: "#12242d", fontWeight: "700" }}
          >
            <HStack spacing={2} justify="center" w="full">
              <FaShieldAlt />
              <Text>Alertas</Text>
              <Badge colorScheme="red" borderRadius="full">
                {panicEvents.length}
              </Badge>
            </HStack>
          </Tab>
          <Tab
            aria-label="Ocorrencias"
            color="whiteAlpha.900"
            borderRadius="full"
            _selected={{ bg: "rgba(255, 255, 255, 0.93)", color: "#12242d", fontWeight: "700" }}
          >
            <HStack spacing={2} justify="center" w="full">
              <FaExclamationTriangle />
              <Text>Chamados</Text>
              <Badge colorScheme="orange" borderRadius="full">
                {ocorrenciaEventos.length}
              </Badge>
            </HStack>
          </Tab>
          {includeIA && (
            <Tab
              aria-label="IA"
              color="whiteAlpha.900"
              borderRadius="full"
              _selected={{ bg: "rgba(255, 255, 255, 0.93)", color: "#12242d", fontWeight: "700" }}
            >
              <HStack spacing={2} justify="center" w="full">
                <FaRobot />
                <Text>IA</Text>
                <Badge colorScheme="purple" borderRadius="full">
                  {aiEventos.length}
                </Badge>
              </HStack>
            </Tab>
          )}
        </TabList>

        <TabPanels flex="1" minH="0">
          <TabPanel px={0} pt={3} h="100%">
            <Box className="camera-events-scroll">
              {panicEvents.length === 0 ? (
                <Text fontSize="sm" color="whiteAlpha.700">
                  Nenhum panico ativo.
                </Text>
              ) : (
                <Stack spacing={2}>
                  {panicEvents.map((event) => (
                    <Box
                      key={`panel-${event.id}`}
                      border="1px solid"
                      borderColor="rgba(255, 255, 255, 0.12)"
                      borderRadius="xl"
                      p={3}
                      bg="rgba(255,255,255,0.04)"
                    >
                      <Text fontSize="sm" color="white" fontWeight="semibold">
                        Panico - {usuarioNomeMap.get(event.usuario_id) || `Usuario ${event.usuario_id}`}
                      </Text>
                      <Text fontSize="xs" color="whiteAlpha.700">
                        Camera {event.camera_id || "-"}
                      </Text>
                      <Text fontSize="xs" color="whiteAlpha.600" mt={1}>
                        {formatEventTime(event)}
                      </Text>
                      <HStack mt={2} spacing={2}>
                        <Button size="xs" colorScheme="red" variant="outline" onClick={() => openPanicEvent(event)}>
                          Ver
                        </Button>
                        {canResolvePanic(event) && (
                          <Button size="xs" colorScheme="gray" onClick={() => resolvePanic(event)}>
                            Encerrar
                          </Button>
                        )}
                      </HStack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </TabPanel>
          <TabPanel px={0} pt={3} h="100%">
            <Box className="camera-events-scroll">
              <Button size="sm" colorScheme="orange" variant="outline" onClick={openOcorrenciaDialog} mb={3}>
                Novo chamado
              </Button>
              {ocorrenciaEventos.length === 0 ? (
                <Text fontSize="sm" color="whiteAlpha.700">
                  Nenhuma ocorrencia registrada.
                </Text>
              ) : (
                <Stack spacing={2}>
                  {ocorrenciaEventos.map((event) => (
                    <Box
                      key={`occ-${event.id}`}
                      border="1px solid"
                      borderColor="rgba(255, 255, 255, 0.12)"
                      borderRadius="xl"
                      p={3}
                      bg="rgba(255,255,255,0.04)"
                    >
                      <HStack spacing={2} mb={1}>
                        {normalizeTipo(event.tipo).includes("incendio") && <FaFire color="#ED8936" />}
                        {normalizeTipo(event.tipo).includes("falta") && <FaBolt color="#ED8936" />}
                        {normalizeTipo(event.tipo).includes("acidente") && <FaCarCrash color="#ED8936" />}
                        {normalizeTipo(event.tipo).includes("buraco") && <FaRoad color="#ED8936" />}
                        <Text fontSize="sm" color="white" fontWeight="semibold">
                          {event.tipo}
                        </Text>
                      </HStack>
                      <Text fontSize="xs" color="whiteAlpha.700">
                        {usuarioNomeMap.get(event.usuario_id) || `Usuario ${event.usuario_id}`} - Camera {event.camera_id || "-"}
                      </Text>
                      {event.descricao && (
                        <Text fontSize="xs" color="whiteAlpha.800" mt={1}>
                          {event.descricao}
                        </Text>
                      )}
                      <Text fontSize="xs" color="whiteAlpha.600" mt={1}>
                        {formatEventTime(event)}
                      </Text>
                      <HStack mt={2} spacing={2}>
                        <Button size="xs" colorScheme="orange" variant="outline" onClick={() => openPanicEvent(event)}>
                          Ver
                        </Button>
                        {canResolveOcorrencia(event) && (
                          <Button size="xs" colorScheme="gray" onClick={() => resolveOcorrencia(event)}>
                            Encerrar
                          </Button>
                        )}
                      </HStack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </TabPanel>
          {includeIA && (
            <TabPanel px={0} pt={3} h="100%">
              <Box className="camera-events-scroll">
                {aiEventos.length === 0 ? (
                  <Text fontSize="sm" color="whiteAlpha.700">
                    Nenhum evento de IA.
                  </Text>
                ) : (
                  <Stack spacing={2}>
                    {aiEventos.map((event) => {
                      const imageUrl = buildImageUrl(event);
                      const confidenceLabel =
                        typeof event.confidence === "number"
                          ? `${Math.round(event.confidence * 100)}%`
                          : null;
                      return (
                        <Box
                          key={`ai-${event.id}`}
                          border="1px solid"
                          borderColor="rgba(255, 255, 255, 0.12)"
                          borderRadius="xl"
                          p={3}
                          bg="rgba(255,255,255,0.04)"
                        >
                          <HStack spacing={2} mb={1}>
                            <FaRobot color="#B794F4" />
                            <Text fontSize="sm" color="white" fontWeight="semibold">
                              {event.tipo || "Violacao"}
                            </Text>
                          </HStack>
                          <Text fontSize="xs" color="whiteAlpha.700">
                            {usuarioNomeMap.get(event.usuario_id) || `Usuario ${event.usuario_id}`} - Camera {event.camera_id || "-"}
                          </Text>
                          {event.descricao && (
                            <Text fontSize="xs" color="whiteAlpha.800" mt={1}>
                              {event.descricao}
                            </Text>
                          )}
                          {confidenceLabel && (
                            <Text fontSize="xs" color="whiteAlpha.700" mt={1}>
                              Confianca {confidenceLabel}
                            </Text>
                          )}
                          <Text fontSize="xs" color="whiteAlpha.600" mt={1}>
                            {formatEventTime(event)}
                          </Text>
                          {imageUrl && (
                            <Image
                              src={imageUrl}
                              alt="Violacao"
                              mt={2}
                              borderRadius="lg"
                              maxH="140px"
                              objectFit="cover"
                              width="100%"
                            />
                          )}
                          <HStack mt={2} spacing={2}>
                            <Button size="xs" colorScheme="purple" variant="outline" onClick={() => openPanicEvent(event)}>
                              Ver
                            </Button>
                            {canResolveAi(event) && (
                              <Button size="xs" colorScheme="gray" onClick={() => resolveAiEvent(event)}>
                                Encerrar
                              </Button>
                            )}
                          </HStack>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </Box>
  );

  const renderPanicDialog = () => (
    <AlertDialog
      isOpen={panicDialogOpen}
      leastDestructiveRef={panicCancelRef}
      onClose={cancelPanicDialog}
      isCentered
    >
      <AlertDialogOverlay bg="rgba(1, 14, 22, 0.72)" backdropFilter="blur(6px)" />
      <AlertDialogContent className="camera-panic-dialog">
        <AlertDialogHeader px={5} pt={5} pb={2}>
          <HStack justify="space-between" spacing={3}>
            <HStack spacing={2}>
              <Box as={FaShieldAlt} color="#99f6e4" />
              <Text fontSize="lg" fontWeight="800" color="white">
                Confirmar panico
              </Text>
            </HStack>
            <Badge colorScheme="red" borderRadius="full" px={2} py={1}>
              {panicCountdown}s
            </Badge>
          </HStack>
        </AlertDialogHeader>
        <AlertDialogBody px={5} pb={3}>
          <Text fontSize="sm" color="whiteAlpha.900">
            O panico sera acionado automaticamente em {panicCountdown}s.
          </Text>
          <Text fontSize="xs" color="whiteAlpha.700" mt={2}>
            Toque em cancelar para interromper.
          </Text>
        </AlertDialogBody>
        <AlertDialogFooter px={5} pb={5} pt={2}>
          <Button ref={panicCancelRef} className="camera-panic-cancel-btn" onClick={cancelPanicDialog}>
            Cancelar
          </Button>
          <Button className="camera-panic-confirm-btn" onClick={confirmPanic} ml={3}>
            Acionar agora
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const renderOcorrenciaDialog = () => (
    <AlertDialog isOpen={ocorrenciaDialogOpen} onClose={closeOcorrenciaDialog} isCentered>
      <AlertDialogOverlay />
      <AlertDialogContent>
        <AlertDialogHeader fontSize="lg" fontWeight="bold">
          Nova ocorrência
        </AlertDialogHeader>
        <AlertDialogBody>
          <Stack spacing={3}>
            <FormControl isRequired>
              <FormLabel>Usuario</FormLabel>
              <Select
                value={ocorrenciaForm.usuario_id}
                onChange={(e) =>
                  setOcorrenciaForm((prev) => ({
                    ...prev,
                    usuario_id: e.target.value,
                    camera_id: ""
                  }))
                }
              >
                <option value="">Selecione</option>
                {usuariosFiltrados.map((user) => (
                  <option key={user.usuario_id} value={user.usuario_id}>
                    {user.nome || `Usuario ${user.usuario_id}`}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Camera</FormLabel>
              <Select
                value={ocorrenciaForm.camera_id}
                onChange={(e) => setOcorrenciaForm((prev) => ({ ...prev, camera_id: e.target.value }))}
              >
                <option value="">Selecione</option>
                {(usuariosFiltrados.find(
                  (user) => Number(user.usuario_id) === Number(ocorrenciaForm.usuario_id)
                )?.cameras || []
                ).map((camera) => (
                  <option key={camera.id} value={camera.id}>
                    {camera.nome || `Camera ${camera.canal || camera.id}`}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Tipo</FormLabel>
              <Select
                value={ocorrenciaForm.tipo}
                onChange={(e) => setOcorrenciaForm((prev) => ({ ...prev, tipo: e.target.value }))}
              >
                <option value="Incendio">Incendio</option>
                <option value="Falta de luz">Falta de luz</option>
                <option value="Acidente">Acidente</option>
                <option value="Buraco">Buraco</option>
                <option value="Outros">Outros</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Descricao</FormLabel>
              <Textarea
                value={ocorrenciaForm.descricao}
                onChange={(e) => setOcorrenciaForm((prev) => ({ ...prev, descricao: e.target.value }))}
              />
            </FormControl>
          </Stack>
        </AlertDialogBody>
        <AlertDialogFooter>
          <Button onClick={closeOcorrenciaDialog} variant="ghost">
            Cancelar
          </Button>
          <Button colorScheme="orange" onClick={handleOcorrenciaSave} ml={3} isLoading={ocorrenciaSaving}>
            Registrar
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const usuarioAtual = usuariosFiltrados[indexAtual] || null;
  const cameraLatAtual = coerceCoord(cameraAtual?.latitude);
  const cameraLngAtual = coerceCoord(cameraAtual?.longitude);
  const usuarioLatAtual = coerceCoord(usuarioAtual?.latitude);
  const usuarioLngAtual = coerceCoord(usuarioAtual?.longitude);
  const cameraDistanceFromUser =
    cameraLatAtual !== null &&
    cameraLngAtual !== null &&
    usuarioLatAtual !== null &&
    usuarioLngAtual !== null
      ? distanceKm(
          { lat: cameraLatAtual, lng: cameraLngAtual },
          { lat: usuarioLatAtual, lng: usuarioLngAtual }
        )
      : null;
  const shouldPreferUserLocation =
    cameraDistanceFromUser !== null && Number.isFinite(cameraDistanceFromUser) && cameraDistanceFromUser > 10;
  const locationLat = shouldPreferUserLocation
    ? usuarioLatAtual
    : cameraLatAtual !== null
      ? cameraLatAtual
      : usuarioLatAtual;
  const locationLng = shouldPreferUserLocation
    ? usuarioLngAtual
    : cameraLngAtual !== null
      ? cameraLngAtual
      : usuarioLngAtual;
  const hasLocation = locationLat !== null && locationLng !== null;
  const clockLabel = `${modalClock.toLocaleDateString("pt-BR")} ${modalClock.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit"
  })}`;
  const cameraCode = cameraAtual
    ? String(cameraAtual?.canal || cameraAtual?.id || cameraIndex + 1).padStart(2, "0")
    : "--";
  const cameraHeaderLabel = `CAM ${cameraCode}`;
  const eventsModalCount = panicEvents.length + ocorrenciaEventos.length;

  const renderAlertsModal = () => (
    <Modal
      isOpen={eventsModal.isOpen}
      onClose={eventsModal.onClose}
      size="2xl"
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="rgba(1, 14, 22, 0.76)" backdropFilter="blur(8px)" />
      <ModalContent
        bg="transparent"
        m={0}
        boxShadow="none"
        maxW={{ base: "100vw", md: "860px" }}
        h={{ base: "100dvh", md: "auto" }}
      >
        <ModalBody p={{ base: 3, md: 4 }} h="100%">
          <Box className="camera-alerts-modal-shell">
            <HStack justify="space-between" align="center">
              <HStack spacing={3}>
                <Box as={FaBell} color="#9ef0f3" />
                <Text color="white" fontWeight="700">
                  Alertas e ocorrencias
                </Text>
                <Badge colorScheme="red" borderRadius="full">
                  {eventsModalCount}
                </Badge>
              </HStack>
              <IconButton
                aria-label="Fechar alertas"
                icon={<Text fontSize="xl" lineHeight="1">x</Text>}
                variant="ghost"
                color="white"
                borderRadius="full"
                onClick={eventsModal.onClose}
              />
            </HStack>
            <Box mt={3} minH="0" h={{ base: "calc(100% - 44px)", md: "66vh" }}>
              {renderEventsPanel({ includeIA: false })}
            </Box>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );

  const renderModal = () =>
    modalAberto &&
    usuarioAtual && (
      <Modal isOpen={modalAberto} onClose={fecharModal} size="full" motionPreset="slideInBottom">
        <ModalOverlay bg="rgba(1, 14, 22, 0.72)" backdropFilter="blur(6px)" />
        <ModalContent bg="transparent" m={0} maxW="100vw" h="100dvh" boxShadow="none">
          <ModalBody p={{ base: 3, md: 4 }} h="100%" overflow="hidden">
            <Box className="camera-modal-shell">
              <Flex
                className="camera-modal-scroll"
                direction="column"
                gap={{ base: 3, md: 4 }}
                h="100%"
              >
                <Stack spacing={{ base: 3, md: 4 }} flex="1" minW="0">
                  <Box className="camera-header-card">
                    <Flex align="center" gap={{ base: 2, md: 4 }}>
                      <Box className="camera-header-avatar">
                        <Image
                          src={`${apiBase}/usuarios/${usuarioAtual.usuario_id}.png`}
                          alt={usuarioAtual.nome || `Usuario ${usuarioAtual.usuario_id}`}
                          boxSize={{ base: "58px", md: "88px" }}
                          borderRadius="full"
                          objectFit="cover"
                        />
                      </Box>
                      <Box flex="1" minW="0">
                        <Text
                          className="camera-header-name"
                          fontSize={{ base: "lg", md: "3xl" }}
                          noOfLines={1}
                        >
                          {usuarioAtual.nome || `Usuario ${usuarioAtual.usuario_id}`}
                        </Text>
                        <HStack className="camera-header-location" spacing={2} mt={1}>
                          <Box as={FaMapMarkedAlt} />
                          <Text fontSize={{ base: "xs", md: "lg" }} noOfLines={1}>
                            {usuarioAtual.cidade || usuarioAtual.endereco || "Rede monitorada"}
                          </Text>
                        </HStack>
                      </Box>
                      <HStack spacing={{ base: 1, md: 2 }} alignSelf="flex-start">
                        <Box position="relative">
                          <IconButton
                            aria-label="Abrir alertas"
                            icon={<FaBell />}
                            variant="ghost"
                            color="white"
                            size="sm"
                            borderRadius="full"
                            _hover={{ bg: "whiteAlpha.200" }}
                            onClick={eventsModal.onOpen}
                          />
                          <Badge
                            colorScheme="red"
                            borderRadius="full"
                            position="absolute"
                            top="-4px"
                            right="-4px"
                            minW={{ base: "16px", md: "18px" }}
                            fontSize={{ base: "0.62rem", md: "0.72rem" }}
                            px={1}
                            textAlign="center"
                          >
                            {eventsModalCount}
                          </Badge>
                        </Box>
                        <IconButton
                          aria-label="Fechar"
                          icon={<Text fontSize="2xl" lineHeight="1">x</Text>}
                          variant="ghost"
                          color="white"
                          size="sm"
                          onClick={fecharModal}
                          borderRadius="full"
                          _hover={{ bg: "whiteAlpha.200" }}
                        />
                      </HStack>
                    </Flex>
                  </Box>

                  <Box className="camera-action-row">
                    <Button
                      className="camera-action-btn"
                      leftIcon={<FaPhone />}
                      onClick={() => {
                        if (!usuarioAtual.celular) return;
                        window.open(`tel:${usuarioAtual.celular}`, "_self");
                      }}
                      isDisabled={!usuarioAtual.celular}
                    >
                      Ligar vizinho
                    </Button>
                    <Button
                      className="camera-action-btn"
                      leftIcon={<FaShieldAlt />}
                      onClick={() => window.open("tel:190", "_self")}
                    >
                      Pedir apoio
                    </Button>
                    <Button
                      className="camera-action-btn"
                      leftIcon={<FaMapMarkedAlt />}
                      onClick={() => {
                        if (!hasLocation) return;
                        window.open(
                          `https://www.google.com/maps/dir/?api=1&destination=${locationLat},${locationLng}`,
                          "_blank"
                        );
                      }}
                      isDisabled={!hasLocation}
                    >
                      Ir ao local
                    </Button>
                  </Box>

                  <Flex gap={3} wrap="wrap">
                    <Button
                      className="camera-nav-btn"
                      leftIcon={<FaArrowLeft />}
                      onClick={usuarioAnterior}
                      isDisabled={usuariosFiltrados.length <= 1}
                    >
                      <Text as="span" className="camera-nav-label">Vizinho anterior</Text>
                    </Button>
                    <Button
                      className="camera-nav-btn"
                      rightIcon={<FaArrowRight />}
                      onClick={proximoUsuario}
                      isDisabled={usuariosFiltrados.length <= 1}
                    >
                      <Text as="span" className="camera-nav-label">Proximo vizinho</Text>
                    </Button>
                  </Flex>

                  <Box className="camera-live-card">
                    <Flex
                      className="camera-live-top"
                      justify="space-between"
                      align="center"
                      gap={3}
                      wrap="wrap"
                    >
                      <HStack spacing={2}>
                        <Badge colorScheme="green" borderRadius="full" px={2} py={1}>
                          {cameraHeaderLabel}
                        </Badge>
                        <Text fontWeight="700" color="white">
                          AO VIVO
                        </Text>
                      </HStack>
                      <Text color="whiteAlpha.900" fontSize={{ base: "xs", md: "sm" }}>
                        {clockLabel}
                      </Text>
                    </Flex>

                    <Box className="camera-live-player-wrap" position="relative">
                      <Badge
                        colorScheme="green"
                        borderRadius="full"
                        px={3}
                        py={1}
                        position="absolute"
                        top={{ base: "10px", md: "14px" }}
                        left={{ base: "10px", md: "14px" }}
                        zIndex="3"
                      >
                        REC
                      </Badge>

                      <IconButton
                        icon={<FaArrowLeft />}
                        onClick={cameraAnterior}
                        isDisabled={camerasAtivas.length <= 1}
                        aria-label="Camera anterior"
                        className="camera-arrow-btn"
                        position="absolute"
                        top="50%"
                        left={{ base: "8px", md: "14px" }}
                        transform="translateY(-50%)"
                        zIndex="3"
                      />
                      <IconButton
                        icon={<FaArrowRight />}
                        onClick={proximaCamera}
                        isDisabled={camerasAtivas.length <= 1}
                        aria-label="Proxima camera"
                        className="camera-arrow-btn"
                        position="absolute"
                        top="50%"
                        right={{ base: "8px", md: "14px" }}
                        transform="translateY(-50%)"
                        zIndex="3"
                      />

                      {loadingVideo && (
                        <Box
                          position="absolute"
                          inset="0"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          bg="rgba(2, 8, 14, 0.45)"
                          zIndex="2"
                          color="white"
                          fontWeight="700"
                        >
                          Carregando video da camera...
                        </Box>
                      )}

                      <video
                        ref={videoRef}
                        controls
                        autoPlay
                        playsInline
                        webkit-playsinline="true"
                        style={{
                          width: "100%",
                          borderRadius: "22px",
                          minHeight: "220px",
                          maxHeight: "56vh",
                          background: "#050f16"
                        }}
                      />
                    </Box>

                    <Flex className="camera-live-nav" justify="space-between" gap={2} mt={3} wrap="wrap">
                      <Button
                        className="camera-live-nav-btn"
                        variant="ghost"
                        color="whiteAlpha.900"
                        leftIcon={<FaArrowLeft />}
                        onClick={cameraAnterior}
                        isDisabled={camerasAtivas.length <= 1}
                      >
                        <Text as="span" className="camera-nav-label">Camera anterior</Text>
                      </Button>
                      <Button
                        className="camera-live-nav-btn"
                        variant="ghost"
                        color="whiteAlpha.900"
                        rightIcon={<FaArrowRight />}
                        onClick={proximaCamera}
                        isDisabled={camerasAtivas.length <= 1}
                      >
                        <Text as="span" className="camera-nav-label">Proxima camera</Text>
                      </Button>
                    </Flex>

                    {!camerasAtivas.length && (
                      <Text mt={3} color="whiteAlpha.700" fontSize="sm">
                        Nenhuma camera ativa para este usuario.
                      </Text>
                    )}

                    {camerasAtivas.length > 1 && (
                      <Flex className="camera-chip-scroll" gap={2} mt={3}>
                        {camerasAtivas.map((camera, idx) => (
                          <Button
                            key={camera.id}
                            className="camera-quick-btn"
                            size="sm"
                            borderRadius="full"
                            variant={idx === cameraIndex ? "solid" : "outline"}
                            colorScheme={idx === cameraIndex ? "teal" : "gray"}
                            onClick={() => setCameraIndex(idx)}
                          >
                            {camera.nome || `Camera ${camera.canal || idx + 1}`}
                          </Button>
                        ))}
                      </Flex>
                    )}

                    <Flex mt={4} gap={3} wrap="wrap" justify="center">
                      <Button
                        className="camera-alert-btn"
                        onClick={openPanicDialog}
                        isDisabled={!cameraAtual || panicSending}
                      >
                        Alerta comunitario
                      </Button>
                    </Flex>
                  </Box>
                </Stack>
              </Flex>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  const mapContent = shouldUseGoogle ? (
    <LoadScriptNext googleMapsApiKey={googleMapsKey} onError={() => setGoogleFailed(true)}>
      <Box position="relative">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={displayCenter}
          zoom={defaultZoom}
          options={mapOptions}
          onLoad={(map) => {
            mapRef.current = map;
            setGoogleReady(true);
            setTimeout(() => {
              fitBounds();
            }, 200);
          }}
        >
          {raioKm > 0 && mapCenter && (
            <CircleF
              center={mapCenter}
              radius={raioKm * 1000}
              options={{
                strokeColor: "#2f855a",
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: "#68d391",
                fillOpacity: 0.15
              }}
            />
          )}

          {ocorrenciaPointsFiltradas.map((event) => (
            <OverlayView
              key={`occ-${event.id}`}
              position={{ lat: Number(event.latitude), lng: Number(event.longitude) }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <Box
                className="ocorrencia-avatar-wrapper"
                role="button"
                aria-label="Ocorrencia"
                onClick={() => openPanicEvent(event)}
              >
                <Box className="ocorrencia-avatar-ring" />
              </Box>
            </OverlayView>
          ))}

          {aiPoints.map((event) => (
            <OverlayView
              key={`ai-${event.id}`}
              position={{ lat: Number(event.latitude), lng: Number(event.longitude) }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <Box
                className="ai-avatar-wrapper"
                role="button"
                aria-label="Evento IA"
                onClick={() => openPanicEvent(event)}
              >
                <Box className="ai-avatar-ring" />
              </Box>
            </OverlayView>
          ))}

          {panicUserPoints.map((event) => (
            <OverlayView
              key={`panic-${event.id}`}
              position={{ lat: Number(event.latitude), lng: Number(event.longitude) }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <Box
                className="panic-avatar-wrapper"
                role="button"
                aria-label="Alerta de panico"
                onClick={() => openPanicEvent(event)}
              >
                <Box className="panic-avatar-ring" />
                <Image
                  className="panic-avatar-img"
                  src={`${apiBase}/usuarios/${event.usuario_id}.png`}
                  alt="Panico"
                />
              </Box>
            </OverlayView>
          ))}

          {filters.showMarkers &&
            usuariosFiltrados
              .filter((user) => !panicUserIds.has(Number(user?.usuario_id || user?.id || 0)))
              .map((user) => {
                const userId = Number(user?.usuario_id || user?.id || 0);
                if (!userId) return null;
                const markerLat = Number(user.markerLatitude ?? user.latitude);
                const markerLng = Number(user.markerLongitude ?? user.longitude);
                return (
                  <OverlayView
                    key={`user-${userId}`}
                    position={{ lat: markerLat, lng: markerLng }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <Box
                      onClick={() => abrirModalByUserId(userId)}
                      boxSize="48px"
                      borderRadius="full"
                      border="2px solid #ffffff"
                      boxShadow="0 2px 6px rgba(0,0,0,0.25)"
                      overflow="hidden"
                      cursor="pointer"
                      bg="white"
                    >
                      <Image
                        src={`${apiBase}/usuarios/${userId}.png`}
                        alt={user.nome || "Avatar"}
                        boxSize="48px"
                        borderRadius="full"
                        objectFit="cover"
                      />
                    </Box>
                  </OverlayView>
                );
              })}
        </GoogleMap>
        {renderFilters()}
      </Box>
    </LoadScriptNext>
  ) : (
    <Box position="relative">
      <LeafletFallback
        usuarios={usuariosFiltrados}
        plano={plano}
        center={displayCenter}
        userCenter={mapCenter}
        height={height}
        apiBase={apiBase}
        onSelectUser={abrirModalByUserId}
        panicEvents={panicEventsWithCoords}
        ocorrenciaEvents={ocorrenciaPointsFiltradas}
        onSelectPanic={openPanicEvent}
        panicUserIds={panicUserIds}
      />
      {renderFilters()}
    </Box>
  );

  const imageModeContent = (
    <ImageNetworkScene
      backgroundUrl={
        assetUrl(currentVisual?.background_image_url) ||
        assetUrl(branding.home_background_url) ||
        "/sistema/fundo.png"
      }
      title={formatCityHeading(currentVisual?.cidade || cidadeSelecionada, currentVisual?.estado)}
      subtitle={
        currentVisual?.subtitulo && currentVisual.subtitulo !== currentVisual.titulo
          ? currentVisual.subtitulo
          : formatCityHeading(currentVisual?.cidade || cidadeSelecionada, currentVisual?.estado)
      }
      cityLabel={
        formatCityLabel(currentVisual?.cidade || cidadeSelecionada, currentVisual?.estado) ||
        "Cidade configurada"
      }
      users={usuariosModoImagem}
      apiBase={apiBase}
      onSelectUser={abrirModalByUserId}
      panicUserIds={panicUserIds}
      ocorrenciaUserIds={ocorrenciaUserIds}
      accentColor={branding.accent_color || "#7dd3fc"}
      sceneHeight={`calc(100vh - 144px)`}
      loading={mapaLoading}
    />
  );

  return (
    <>
      <Box position="relative">
        <Box minW="0" minH={height || "360px"}>
          {isImageMode ? imageModeContent : mapContent}
        </Box>
        <Button
          position="absolute"
          top={{ base: "16px", md: "18px" }}
          right={{ base: "16px", md: "18px" }}
          zIndex="3"
          leftIcon={<FaBell />}
          colorScheme="green"
          borderRadius="full"
          boxShadow="0 10px 24px rgba(0,0,0,0.18)"
          onClick={eventsDrawer.onOpen}
        >
          Alertas
        </Button>
      </Box>

      <Drawer isOpen={eventsDrawer.isOpen} placement="right" onClose={eventsDrawer.onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader borderBottomWidth="1px">Alertas e chamados</DrawerHeader>
          <DrawerBody p={3}>{renderEventsPanel()}</DrawerBody>
        </DrawerContent>
      </Drawer>
      {renderModal()}
      {renderAlertsModal()}
      {renderPanicDialog()}
      {renderOcorrenciaDialog()}
    </>
  );
};

export default MapaUsuarios;


